// Nurse2Page.tsx
import React, { useEffect, useMemo, useState, JSX } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../../../components/Center Admin/layout/AppLayout";

import { PhysicalExaminationForm } from "../../../components/Center Admin/forms/PhysicalExaminationForm";
import { AnthropometricMeasurementForm } from "../../../components/Center Admin/forms/AnthropometricMeasurementForm";
import { VitalsExaminationForm } from "../../../components/Center Admin/forms/VitalsExaminationForm";
import { BUSampleForm } from "../../../components/Center Admin/forms/BUSampleForm";
import { ADMinorSymptomsExaminationForm } from "../../../components/Center Admin/forms/ADMinorSymptomsExaminationForm";
import BnuSampleForm from "../../../components/Center Admin/forms/sentToLab";
import LabReportForm from "../../../components/Center Admin/forms/LabReportForm";
import { VisionScreeningForm } from "../../../components/Center Admin/forms/VisionScreeningForm";
import { OralImagingForm } from "../../../components/Center Admin/forms/OralImagingForm";
import { SalivaSampleForm } from "../../../components/Center Admin/forms/SalivaSampleForm";
import { CariogramInputForm } from "../../../components/Center Admin/forms/CariogramInputForm";
import AudiologyScreeningForm from "../../../components/Center Admin/forms/AudiologyScreeningForm";

import "../../../center admin css/Nurse2Page.css";
import "../../../center admin css/centerAdmin.css";
import Nurse2ActivePatientCard from "../../../components/Center Admin/Reuse/Nurse2ActivePatientCard";

/** ---------- helpers ---------- */
const normalize = (s?: string) =>
  (s ?? "").toString().trim().toUpperCase().replace(/\s+/g, " ");

type BackendForm = {
  _id?: string;
  name?: string;
  key?: string;
  isActive?: boolean;
};

type AllowedForm = {
  id: string;
  label: string;
  render: (emrId: string) => JSX.Element;
};

/** Robust reader: search both storages and multiple keys */
function loadUserFromStorage(): any | null {
  const keys = [
    "currentUser",
    "CurrrentResponse",
    "CurrentResponse",
    "backendResponse",
  ];
  const stores: Storage[] = [sessionStorage, localStorage];

  const read = (store: Storage, key: string) => {
    try {
      const raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  for (const s of stores) {
    for (const k of keys) {
      const v = read(s, k);
      if (v && typeof v === "object") return v;
    }
  }
  return null;
}

export const Nurse2Page: React.FC = () => {
  const { activePatient } = useOutletContext<any>();

  const emrId = activePatient?.child?.childId || "";

  const canCollectBUSample =
    activePatient?.canCollectBUSample ??
    activePatient?.meta?.canCollectBUSample ??
    false;

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleForms, setVisibleForms] = useState<AllowedForm[]>([]);
  const [activeIdx, setActiveIdx] = useState<number>(0);

  const [formStatus, setFormStatus] = useState({
    vitals: false,
    buSample: false,
  });

  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    console.log(
      "[Nurse2Page] activePatient changed",
      activePatient?.child?.childId,
    );

    setFormStatus({
      vitals: false,
      buSample: false,
    });
    setIsStarted(false);
  }, [activePatient]);

  useEffect(() => {
    const handler = () => {
      setIsStarted(true);
    };

    window.addEventListener("patientUpdated", handler);

    return () => {
      window.removeEventListener("patientUpdated", handler);
    };
  }, []);

  const updateFormStatus = (key: string, value: boolean) => {
    setFormStatus((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const validateBeforeComplete = () => {
    if (!formStatus.vitals) {
      alert("Please complete Vitals Examination form");
      return false;
    }
    return true;
  };

  const FORM_COMPONENTS: Record<string, (emrId: string) => JSX.Element> = {
    ANTHROPOMETRY: (emrId) => <AnthropometricMeasurementForm emrId={emrId} />,
    "ANTHROPOMETRIC MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),
    "ANTHROPOMETRY MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),

    "PHYSICAL EXAMINATION": (emrId) => (
      <PhysicalExaminationForm emrId={emrId} />
    ),

    "ADMINOR SYSPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "ADMINOR SYMPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "AD MINOR SYMPTOMS": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),

    "BLOOD AND URIN SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),
    "BLOOD AND URINE SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),
    "BLOOD SAMPLE": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),

    "BLOOD SAMPLE SENT TO LAB": (emrId) => <BnuSampleForm emrId={emrId} />,
    "LAB REPORT": (emrId) => <LabReportForm emrId={emrId} />,
    "VISION SCREENING": (emrId) => <VisionScreeningForm emrId={emrId} />,
    "ORAL IMAGING": (emrId) => <OralImagingForm emrId={emrId} />,

    "VITALS EXAMINATION": (emrId) => (
      <VitalsExaminationForm
        emrId={emrId}
        onComplete={() => updateFormStatus("vitals", true)}
      />
    ),

    "CARIOGRAM SALAIVA SAMPLE": (emrId) => <SalivaSampleForm emrId={emrId} />,
    "CARIOGRAM INPUT": (emrId) => <CariogramInputForm emrId={emrId} />,
    "AUDIOLOGY SCREENING": (emrId) => <AudiologyScreeningForm emrId={emrId} />,
  };

  // useEffect(() => {
  //   const handler = () => setIsStarted(true);
  //   window.addEventListener("patientUpdated", handler);
  //   return () => window.removeEventListener("patientUpdated", handler);
  // }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      const user = loadUserFromStorage();
      if (!user) {
        throw new Error("User data not found in session. Please log in again.");
      }

      const fromRole: BackendForm[] =
        user?.roleAssignments?.[0]?.roleId?.formIds ?? [];
      const fallback: BackendForm[] = user?.forms ?? [];
      const backendForms = (fromRole.length ? fromRole : fallback).filter(
        (f) => f?.isActive !== false,
      );

      const built: AllowedForm[] = [];
      const seen = new Set<string>();

      backendForms.forEach((bf) => {
        const nameN = normalize(bf.name);
        const keyN = normalize(bf.key);

        const isBUSampleForm =
          nameN.includes("BLOOD AND URINE SAMPLE") ||
          nameN.includes("BLOOD AND URIN SAMPLE") ||
          keyN.includes("BLOOD AND URINE SAMPLE") ||
          keyN.includes("BLOOD AND URIN SAMPLE");

        if (isBUSampleForm && !canCollectBUSample) {
          return;
        }

        const renderer =
          FORM_COMPONENTS[nameN] ?? FORM_COMPONENTS[keyN] ?? null;
        if (!renderer) return;

        const label = bf.name || bf.key || "Form";
        const uniqueId = bf._id || `${label}-${keyN || nameN}`;
        if (!seen.has(uniqueId)) {
          seen.add(uniqueId);
          built.push({ id: uniqueId, label, render: renderer });
        }
      });

      // setVisibleForms(built);
      // setActiveIdx((prev) => (built[prev] ? prev : 0));

      // Keep the existing backend form order,
      // but always move B&U Sample to the last tab.
      const orderedForms = [...built].sort((a, b) => {
        const aLabel = normalize(a.label);
        const bLabel = normalize(b.label);

        const isABUSample =
          aLabel.includes("B&U SAMPLE") ||
          aLabel.includes("BLOOD AND URINE SAMPLE") ||
          aLabel.includes("BLOOD AND URIN SAMPLE");

        const isBBUSample =
          bLabel.includes("B&U SAMPLE") ||
          bLabel.includes("BLOOD AND URINE SAMPLE") ||
          bLabel.includes("BLOOD AND URIN SAMPLE");

        if (isABUSample && !isBBUSample) return 1;
        if (!isABUSample && isBBUSample) return -1;

        return 0;
      });

      setVisibleForms(orderedForms);
      setActiveIdx((prev) => (orderedForms[prev] ? prev : 0));

      if (built.length === 0) {
        setTimeout(() => alert("No forms are assigned to your role."), 0);
      }
    } catch (err: any) {
      setError(err.message || "Could not load permitted forms.");
      setVisibleForms([]);
      setActiveIdx(0);
    } finally {
      setLoading(false);
    }
  }, [canCollectBUSample]);

  return (
    <section className="bnu-main nurse2">
      <Nurse2ActivePatientCard
        onPatientSelect={(patient) => console.log("Selected:", patient)}
        showActions={true}
        validateBeforeComplete={validateBeforeComplete}
        formStatus={formStatus}
        onStationStarted={() => setIsStarted(true)}
      />

      <div className="nurse2-subtabs" role="tablist">
        {loading && <div className="nurse2-subtab disabled">Loading…</div>}
        {error && <div className="nurse2-subtab disabled">{error}</div>}
        {!loading && !error && visibleForms.length > 0 && (
          <>
            {visibleForms.map((form, i) => (
              <button
                key={form.id}
                type="button"
                className={`nurse2-subtab${activeIdx === i ? " active" : ""}`}
                onClick={() => setActiveIdx(i)}
              >
                {form.label}
              </button>
            ))}
          </>
        )}
        {!loading && !error && visibleForms.length === 0 && (
          <div className="nurse2-subtab disabled">No forms assigned</div>
        )}
      </div>

      {activePatient &&
        isStarted &&
        !loading &&
        !error &&
        visibleForms[activeIdx] && <>{visibleForms[activeIdx].render(emrId)}</>}
    </section>
  );
};
